/**
 * Account sign-in (email/phone OTP) copy.
 */
export const auth = {
    login: {
      title: 'Sign in to Adieuu',
      subtitle: 'Enter your email or phone to receive a verification code.',
      emailPlaceholder: 'Email or phone number',
      submitButton: 'Send code',
      sending: 'Sending...',
    },
    instances: {
      connectedTo: 'Connecting to {{label}}',
      selfHostedLink: 'Self-Hosted Login',
      switchLink: 'Switch instance',
      selfHostedActive: {
        title: 'You are signing in to a self-hosted server',
        body: '{{label}} is not operated by Adieuu. By connecting you are trusting the operator with your data and they can see anything you send that is not end-to-end encrypted. Use at your own risk.',
      },
      choose: {
        title: 'Choose an instance',
        subtitle: 'Pick the server to sign in to.',
        defaultBadge: 'Official',
        officialDescription: 'Operated by Adieuu',
        activeLabel: 'Currently selected',
        selfHostedBadge: 'Self-hosted',
        selfHostedNote: 'Self-hosted servers are run by whoever operates them, not by Adieuu.',
        continueButton: 'Continue',
        addButton: 'Add a self-hosted instance',
        removeButton: 'Remove',
        removeConfirm: 'Remove {{label}}? Anything stored locally for this instance stays until you sign in again.',
        empty: 'No instances available.',
        loading: 'Loading instances...',
      },
      add: {
        title: 'Add a self-hosted instance',
        subtitle: 'Enter the address of the instance you want to connect to. Currently only HTTPS is supported - LAN, etc may be added in the future',
        labelField: 'Display name',
        labelPlaceholder: 'Example Corp',
        labelHint: 'Optional. Defaults to the name the server reports.',
        apiField: 'API address',
        apiPlaceholder: 'https://api.example.com',
        apiHint: 'HTTPS only. We attempt to look up the rest of the settings from this address.',
        advancedToggleShow: 'Show advanced settings',
        advancedToggleHide: 'Hide advanced settings',
        advancedHint:
          'Only needed if automatic lookup fails. Leave blank to derive from the API address.',
        chatField: 'Chat WebSocket URL',
        chatPlaceholder: 'wss://api.example.com/ws/chat',
        appOriginField: 'Web app origin',
        appOriginPlaceholder: 'https://app.example.com',
        mediaField: 'Media origin',
        e2eMediaField: 'Encrypted media origin',
        livekitField: 'Call server URL',
        submitButton: 'Add instance',
        submitting: 'Connecting...',
        cancelButton: 'Cancel',
        warningTitle: 'Self-hosted servers are NOT operated by Adieuu',
        warningBody:
          'By connecting you are trusting the operator with your data and they can see anything you send that is not end-to-end encrypted. Please be careful!',
        discoveryFailed:
          'We could not read the settings from that address. Check for typos or configuration errors — advanced settings is now open so you can enter the remaining details.',
        discoveryFailedOpen:
          'We could not read the settings from that address. Check for typos or configuration errors, or enter the remaining details in the fields below.',
      },
      errors: {
        invalid_url: 'That does not look like a valid address.',
        insecure_url: 'Only HTTPS addresses are allowed.',
        timeout: 'The server took too long to respond.',
        unreachable: 'We could not reach that server.',
        http_error: 'The server rejected the request.',
        invalid_response: 'That server did not return a valid instance configuration.',
        unsupported_version: 'That server needs a newer version of this app.',
        not_found: 'That instance is no longer available.',
        immutable: 'The built-in instance cannot be changed.',
        duplicate: 'That instance has already been added.',
        limit_reached: 'You have reached the maximum number of saved instances.',
        persist_failed: 'We could not save your instance settings. Please try again.',
        unknown: 'Something went wrong. Please try again.',
      },
    },
    verify: {
      title: 'Enter verification code',
      subtitle: 'We sent a code to {{identifier}}',
      verifyButton: 'Verify',
      verifying: 'Verifying...',
      resendCode: 'Resend code',
      changeIdentifier: 'Use a different email or phone',
    },
    restriction: {
      bannedTitle: 'Account Banned',
      ofacBannedTitle: 'Account Banned While Sanctioned',
      suspendedTitle: 'Account Suspended',
      bannedMessage: 'You are banned from Adieuu.',
      bannedMessageWithReason: 'You are banned. {{reason}}',
      bannedMessageWithCategory: 'You are banned for {{category}}.',
      suspendedSubtitle: 'Your account access is temporarily restricted.',
      reason: 'Reason',
      timeRemaining: 'Time remaining',
      expiredMessage: 'Your suspension has expired. You may sign in again. Be better.',
      backToLogin: 'Back to sign in',
      appealMessage: 'If you believe this was made in error, you may appeal by emailing',
      appealEmail: 'consequences@adieuu.com',
      appealInstructions: 'with your account details.',
      ofacMessage:
        'You connected from an IP address associated with a country subject to US sanctions. We are unable to provide service. Appeals are not available.',
      category: {
        tos_violation: 'ToS violations',
        spam: 'spam',
        harassment: 'harassment',
        hate_speech: 'hate speech',
        violence: 'violence',
        illegal_content: 'illegal content',
        csam: 'child safety violations',
        impersonation: 'impersonation',
        fraud: 'fraud',
        security_abuse: 'security abuse',
        ofac_sanctioned: 'sanctions compliance',
        ofac_self_attestation: 'export control',
        other: 'policy violations',
      },
    },
} as const;
